// frontend\src\context\AuthContext.jsx
import { createContext, useContext, useState, useEffect } from "react";
import { getProfile } from "../utils/api";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const normalizeProfileImage = (userData) => {
    if (!userData) return userData;
    
    // Clone object agar tidak memodifikasi original
    const normalized = { ...userData };
    
    if (normalized.profile_image && !normalized.profile_image.startsWith("http")) {
      normalized.profile_image = `https://blasterprime.com${normalized.profile_image}`;
    }
    
    return normalized;
  };

  const login = (userData, token, rememberMe = false) => {
    console.log("🔐 LOGIN CALLED:", {
      userData: userData?.email,
      rememberMe,
      hasToken: !!token,
    });

    // Normalize image URL
    const normalizedUser = normalizeProfileImage(userData);
    
    // Hapus semua data yang ada sebelumnya
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");

    setCurrentUser(normalizedUser);

    // Simpan token dan user berdasarkan rememberMe
    if (rememberMe) {
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(normalizedUser));
      console.log("💾 Saved to localStorage (remember me)");
    } else {
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("user", JSON.stringify(normalizedUser));
      console.log("💾 Saved to sessionStorage");
    }
  };

  const logout = () => {
    console.log("🚪 LOGOUT CALLED");
    setCurrentUser(null);
    
    // Hapus dari SEMUA storage
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    
    localStorage.removeItem("rememberedEmail");
    localStorage.removeItem("rememberedPassword");
    localStorage.removeItem("rememberMe");
  };

  const saveRememberedCredentials = (email, password, remember) => {
    try {
      if (remember) {
        localStorage.setItem("rememberedEmail", email);
        localStorage.setItem("rememberedPassword", password);
        localStorage.setItem("rememberMe", "true");
        console.log("💾 Remember credentials saved");
      } else {
        localStorage.removeItem("rememberedEmail");
        localStorage.removeItem("rememberedPassword");
        localStorage.removeItem("rememberMe");
        console.log("🗑️ Remember credentials removed");
      }
    } catch (error) {
      console.error("Error saving remembered credentials:", error);
    }
  };

  const getRememberedCredentials = () => {
    try {
      const email = localStorage.getItem("rememberedEmail");
      const password = localStorage.getItem("rememberedPassword");
      const rememberMe = localStorage.getItem("rememberMe") === "true";
      
      if (rememberMe && email && password) {
        return { email, password, rememberMe };
      }
    } catch (error) {
      console.error("Error getting remembered credentials:", error);
    }
    return null;
  };

const updateUser = (userData) => {
  console.log("🔄 updateUser called with:", userData);
  
  setCurrentUser(prev => {
    // Cek apakah ada perubahan yang signifikan
    const normalizedUserData = normalizeProfileImage(userData);
    const updatedUser = { ...prev, ...normalizedUserData };
    
    // Jangan update jika tidak ada perubahan
    if (JSON.stringify(prev) === JSON.stringify(updatedUser)) {
      console.log("⚠️ No changes detected, skipping update");
      return prev;
    }
    
    console.log("✅ User updated in context:", updatedUser.email);
    
    // Update di storage yang sesuai
    if (localStorage.getItem("user")) {
      localStorage.setItem("user", JSON.stringify(updatedUser));
    } else if (sessionStorage.getItem("user")) {
      sessionStorage.setItem("user", JSON.stringify(updatedUser));
    }
    
    window.dispatchEvent(new CustomEvent('profileUpdated', { 
      detail: updatedUser 
    }));
    
    return updatedUser;
  });
};

  // 🔥 Fungsi baru untuk fetch user terbaru dari API
const refreshUserFromAPI = async () => {
  console.log("🔄 ========== REFRESH USER FROM API START ==========");
  
  try {
    console.log("🔄 Refreshing user data from API using getProfile...");
    
    // 🔥 Gunakan fungsi getProfile yang sudah ada
    const response = await getProfile();
    console.log("📡 getProfile response:", response);
    
    if (response.data?.success) {
      const userData = response.data.data;
      console.log("✅ User data from getProfile:", userData);
      console.log("🖼️ profile_image from API:", userData.profile_image);
      
      if (userData.profile_image) {
        // Normalize image URL
        let imageUrl = userData.profile_image;
        if (!imageUrl.startsWith("http")) {
          imageUrl = `https://blasterprime.com${imageUrl}`;
        }
        
        console.log("🔄 Calling updateUser with profile_image:", imageUrl);
        
        // Update user dengan data lengkap
        const updatedUserData = {
          ...currentUser,
          ...userData,
          profile_image: imageUrl
        };
        
        updateUser(updatedUserData);
        console.log("✅ updateUser called successfully");
      } else {
        console.log("⚠️ No profile_image in API response");
      }
    } else {
      console.log("⚠️ Failed to refresh user data");
    }
  } catch (error) {
    console.error("Error refreshing user:", error);
  } finally {
    console.log("🔄 ========== REFRESH USER FROM API END ==========");
  }
};

  useEffect(() => {
    console.log("🔄 Load user from storage...");
    try {
      // Prioritaskan localStorage (remember me)
      let token = localStorage.getItem("token");
      let user = localStorage.getItem("user");
      let storageSource = "localStorage";

      // Jika tidak ada di localStorage, coba sessionStorage
      if (!token || !user) {
        token = sessionStorage.getItem("token");
        user = sessionStorage.getItem("user");
        storageSource = "sessionStorage";
      }

      if (token && user) {
        const parsedUser = JSON.parse(user);
        const normalizedUser = normalizeProfileImage(parsedUser);
        setCurrentUser(normalizedUser);
        console.log(`✅ User loaded from ${storageSource}:`, normalizedUser.email);
        
        // 🔥 Fetch user terbaru dari API untuk memastikan data lengkap
        setTimeout(() => {
          if (normalizedUser.id) {
            refreshUserFromAPI();
          }
        }, 1000);
      } else {
        console.log("ℹ️ No user found in storage");
      }
    } catch (err) {
      console.error("Auth load error:", err);
      setCurrentUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleProfileUpdated = (event) => {
      console.log("🌀 AuthContext detected profile update event...", event.detail);
      try {
        if (event.detail) {
          const normalizedUser = normalizeProfileImage(event.detail);
          setCurrentUser(normalizedUser);
          console.log("✅ AuthContext updated currentUser:", normalizedUser.email);
        }
      } catch (err) {
        console.error("💥 Failed to update user:", err);
      }
    };

    window.addEventListener("profileUpdated", handleProfileUpdated);
    return () => window.removeEventListener("profileUpdated", handleProfileUpdated);
  }, []);

  return (
    <AuthContext.Provider 
      value={{ 
        currentUser, 
        isLoading, 
        login, 
        logout,
        updateUser,
        refreshUserFromAPI, // 🔥 Export fungsi refresh
        saveRememberedCredentials,
        getRememberedCredentials 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}